// app/src/lib/delivery/statements.js — persist + query delivery statements.
//
// Local-first (Dexie), tenant-scoped by userId. Uploads are capped at
// MONTHLY_UPLOAD_LIMIT per CALENDAR month (counted by upload time, not the
// statement's reporting period) — the cap the user asked for. Delivery is a
// Pro feature; the tab is gated in the UI, and the OCR presign is gated
// server-side, so this layer enforces only the per-month count.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { requireWritable } from '../scale/plan.js';
import { normalizeStatement } from './normalizer.js';
import { BUCKET_KEYS, reconcileResidualCents } from './model.js';

const REVIEW_TOLERANCE_CENTS = 500;

export const MONTHLY_UPLOAD_LIMIT = 3;

const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

/** YYYY-MM for a Date (local time). */
function ymOf(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

async function uid() {
  const claims = await currentClaims();
  if (!claims) throw new Error('delivery: not signed in');
  return claims.sub;
}

/** All statements for the signed-in user (newest reporting month first). */
export async function listStatements() {
  const userId = await uid();
  const rows = await db.deliveryStatements.where('userId').equals(userId).toArray();
  return rows.sort((a, b) => String(b.periodMonth || '').localeCompare(String(a.periodMonth || '')) || b.createdAt - a.createdAt);
}

/** Statements whose reporting period is `periodMonth` (YYYY-MM). */
export async function statementsForMonth(periodMonth) {
  const userId = await uid();
  return db.deliveryStatements.where('[userId+periodMonth]').equals([userId, periodMonth]).toArray();
}

/** How many statements remain for a given reporting month (≈ one per platform). */
export async function monthQuota(periodMonth) {
  if (!periodMonth) return { used: 0, limit: MONTHLY_UPLOAD_LIMIT, remaining: MONTHLY_UPLOAD_LIMIT, periodMonth };
  const userId = await uid();
  const used = await db.deliveryStatements.where('[userId+periodMonth]').equals([userId, periodMonth]).count();
  return { used, limit: MONTHLY_UPLOAD_LIMIT, remaining: Math.max(0, MONTHLY_UPLOAD_LIMIT - used), periodMonth };
}

/**
 * Persist a statement from an OCR delivery extract. The cap is per REPORTING
 * month (≈ one per platform: DoorDash/Uber Eats/Grubhub), so prior months can be
 * backfilled freely. `hint` supplies platform/periodMonth fallbacks when OCR
 * couldn't determine them.
 *
 * @param {Object} rawExtract - OCR processor delivery output
 * @param {Object} [meta] - { platform, periodMonth, receiptObjectKey }
 * @returns {Promise<Object>} the saved statement row
 */
export async function saveStatement(rawExtract, meta = {}) {
  await requireWritable();
  const userId = await uid();

  const canon = normalizeStatement(rawExtract, { platform: meta.platform, periodMonth: meta.periodMonth });

  // Cap per reporting month (the statement's own period), not per upload date.
  if (canon.periodMonth) {
    const used = await db.deliveryStatements.where('[userId+periodMonth]').equals([userId, canon.periodMonth]).count();
    if (used >= MONTHLY_UPLOAD_LIMIT) {
      const err = new Error(`You already have ${MONTHLY_UPLOAD_LIMIT} statements for that month. Delete one to add another.`);
      err.code = 'DELIVERY_UPLOAD_LIMIT';
      throw err;
    }
  }

  const now = Date.now();
  const row = {
    statementId: uuid(),
    userId,
    ...canon,
    receiptObjectKey: meta.receiptObjectKey || null,
    rawExtract: rawExtract || null,
    uploadMonth: ymOf(),
    createdAt: now,
  };
  await db.deliveryStatements.put(row);
  return row;
}

/**
 * Apply user corrections to a statement's (signed) bucket cents. Recomputes the
 * reconciliation residual + review flag directly from the edited buckets — no
 * dollar round-trip — so the displayed values are exactly what the user typed.
 */
export async function updateStatement(statementId, patch = {}) {
  await requireWritable();
  const userId = await uid();
  const existing = await db.deliveryStatements.get(statementId);
  if (!existing || existing.userId !== userId) throw new Error('delivery: statement not found');
  const merged = { ...existing, ...patch };
  for (const k of BUCKET_KEYS) merged[k] = Number.isFinite(merged[k]) ? merged[k] : 0;
  merged.residualCents = reconcileResidualCents(merged);
  merged.needsReview = !merged.platform || !merged.periodMonth
    || merged.grossSalesCents <= 0 || merged.netPayoutCents === 0
    || Math.abs(merged.residualCents) > REVIEW_TOLERANCE_CENTS;
  await db.deliveryStatements.put(merged);
  return merged;
}

/** Delete a statement (does not refund the monthly upload count). */
export async function deleteStatement(statementId) {
  await requireWritable();
  const userId = await uid();
  const existing = await db.deliveryStatements.get(statementId);
  if (!existing || existing.userId !== userId) return;
  await db.deliveryStatements.delete(statementId);
}
